/**
 * 顧客詳細「所有商品」タブ。
 *
 * 機能:
 *  - 所有機器一覧 (useSoldDevices({ contactId }))
 *  - 「+ 機器追加」: シリアル未入力 / 未完了の deliveries を一覧化し、シリアルを登録 →
 *     status='completed' に更新すると onDeliveryCompleted Trigger が
 *     sold_devices に1台ずつ起票する
 *  - 譲渡操作は既存の `/sold-devices` 画面に集約 (ここではリンクのみ)
 *  - 「商談を開く」は戻り先 (顧客詳細) を渡す。商談詳細の「前の画面へ」でこのタブに戻れる (2026-10-30)
 */
import type { Delivery, DeliveryLineItem } from '@goenhub/shared';
import { countDeliverySerials, padSerialNumbers, serialFieldLabel } from '@goenhub/shared';
import { useQuery } from '@tanstack/react-query';
import { collectionGroup, getDocs, orderBy, query, where } from 'firebase/firestore';
import { Cpu, ArrowRight, Plus, Repeat, AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useDeals } from '@/hooks/useDeals';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useSoldDevices, useUpdateDelivery } from '@/hooks/useSalesOps';
import { db } from '@/lib/firebase';
import { paths } from '@/routes/paths';

interface Props {
  contactId: string;
}

export function ContactDevicesTab({ contactId }: Props) {
  // T4: 「自分が使用中」+「親として所有 (子が使用)」両方を取得
  const devices = useSoldDevices({ contactId, includeAsOwner: true });
  const deals = useDeals({ contactId });
  const returnToState = useReturnToState('顧客詳細');
  const [serialDialogOpen, setSerialDialogOpen] = useState(false);

  const isLoading = devices.isLoading || deals.isLoading;
  const hasAnyDeal = (deals.data ?? []).length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">所有機器</h3>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            納品完了で自動登録 + シリアル登録から手動でも登録可能。譲渡は{' '}
            <Link to={paths.soldDevices} className="text-[var(--accent)] hover:underline">
              稼働機器画面
            </Link>{' '}
            から。
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setSerialDialogOpen(true)}
          disabled={!hasAnyDeal}
        >
          <Plus className="size-3.5" />
          機器追加 (シリアル登録)
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !devices.data || devices.data.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title="所有機器なし"
          description="納品完了で自動登録されます。シリアル未入力の機器がある場合は「+ 機器追加」から登録してください。"
        />
      ) : (
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <div className="grid grid-cols-[2fr_1.2fr_1fr_1fr_0.8fr] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <div>商品</div>
            <div>シリアル</div>
            <div>設置日</div>
            <div>由来商談</div>
            <div>譲渡履歴</div>
          </div>
          {devices.data.map((d, i) => {
            // T4: この顧客がこの行にとってどんな立場かを判定
            const isUser = d.contactId === contactId;
            const isOwner = d.ownerContactId === contactId;
            const hasParent = d.ownerContactId && d.ownerContactId !== d.contactId;
            return (
              <div
                key={d.id}
                className={`grid grid-cols-[2fr_1.2fr_1fr_1fr_0.8fr] gap-3 px-4 py-2.5 items-center text-sm ${
                  i < devices.data.length - 1 ? 'border-b border-[var(--border)]' : ''
                } ${d.decommissioned ? 'opacity-50' : ''}`}
                title={d.decommissioned ? 'アーカイブ済み機器' : undefined}
              >
                <div className="min-w-0">
                  <div className="font-medium text-[var(--text-primary)] truncate flex items-center gap-1.5">
                    <Cpu className="size-3.5 text-[var(--accent-cyan)] flex-shrink-0" />
                    {d.productName}
                    {d.decommissioned && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-muted)]">
                        アーカイブ済
                      </span>
                    )}
                    {/* T4: 親 (所有者) としての行 = 子が実際に使用 */}
                    {hasParent && isOwner && !isUser && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent-purple-subtle,_rgba(155,93,229,0.15))] text-[var(--accent-purple,_#9b5de5)]">
                        親として所有
                      </span>
                    )}
                    {/* T4: 子 (使用者) としての行 = 親が別に存在 */}
                    {hasParent && isUser && !isOwner && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent-cyan-subtle,_rgba(0,187,249,0.15))] text-[var(--accent-cyan,_#00bbf9)]">
                        使用中 (親別)
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] truncate ml-5">
                    {d.modelName}
                    {d.variation ? ` / ${d.variation}` : ''}
                  </div>
                </div>
                <div className="font-mono text-xs text-[var(--text-secondary)] truncate">
                  {d.serialNumber}
                </div>
                <div className="text-xs text-[var(--text-muted)] tabular-nums">
                  {fmtDate(d.installedAt)}
                </div>
                <div>
                  <Link
                    to={paths.dealDetail.replace(':id', d.dealId)}
                    state={returnToState}
                    className="text-xs text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                  >
                    商談を開く
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
                <div>
                  {d.transferHistory.length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[var(--accent-purple)]">
                      <Repeat className="size-3" />
                      {d.transferHistory.length}回
                    </span>
                  ) : (
                    <span className="text-xs text-[var(--text-muted)]">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {serialDialogOpen && (
        <SerialEntryDialog contactId={contactId} onClose={() => setSerialDialogOpen(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// シリアル登録ダイアログ
// ─────────────────────────────────────────────

interface DeliveryWithDeal extends Delivery {
  id: string;
}

function SerialEntryDialog({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const allDeliveries = useContactDeliveries(contactId);
  const update = useUpdateDelivery();

  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string | null>(null);
  const [editedItems, setEditedItems] = useState<DeliveryLineItem[] | null>(null);

  const target = useMemo<DeliveryWithDeal | null>(
    () => allDeliveries.data?.find((d) => d.id === selectedDeliveryId) ?? null,
    [allDeliveries.data, selectedDeliveryId],
  );

  const pendingDeliveries = (allDeliveries.data ?? []).filter(
    (d) => d.status !== 'completed' && d.status !== 'cancelled',
  );

  const handleSelect = (d: DeliveryWithDeal) => {
    setSelectedDeliveryId(d.id);
    // 台数分のシリアル欄を用意する (空配列の発送でも入力欄を出すため)
    setEditedItems(d.lineItems.map((it) => ({ ...it, serialNumbers: padSerialNumbers(it) })));
  };

  const handleChangeSerial = (itemIdx: number, serialIdx: number, value: string) => {
    if (!editedItems) return;
    const next = editedItems.map((it, i) => {
      if (i !== itemIdx) return it;
      const nextSerials = [...it.serialNumbers];
      nextSerials[serialIdx] = value;
      return { ...it, serialNumbers: nextSerials };
    });
    setEditedItems(next);
  };

  const allFilled = useMemo(() => {
    if (!editedItems) return false;
    return countDeliverySerials(editedItems).missing === 0;
  }, [editedItems]);

  const handleSave = async (markCompleted: boolean) => {
    if (!target || !editedItems) return;
    try {
      await update.mutateAsync({
        dealId: target.dealId,
        deliveryId: target.id,
        lineItems: editedItems,
        ...(markCompleted ? { status: 'completed' as const } : {}),
      });
      toast.success(
        markCompleted
          ? '納品完了に更新しました。シリアルから稼働機器を自動登録します。'
          : 'シリアルを保存しました',
      );
      onClose();
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>シリアル番号を登録</DialogTitle>
        </DialogHeader>

        {allDeliveries.isLoading ? (
          <Spinner />
        ) : pendingDeliveries.length === 0 ? (
          <div className="py-6">
            <EmptyState
              icon={AlertTriangle}
              title="登録対象の納品なし"
              description="この顧客に未完了の発送がありません。発送が起票されてから再度開いてください。"
            />
          </div>
        ) : !target ? (
          <div className="space-y-2">
            <p className="text-xs text-[var(--text-muted)]">シリアル未入力の納品を選んでください</p>
            {pendingDeliveries.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => handleSelect(d)}
                className="w-full text-left px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] hover:bg-[var(--bg-hover)] transition-colors"
              >
                <div className="text-sm font-medium">{d.deliveryNumber}</div>
                <div className="text-xs text-[var(--text-muted)] mt-0.5">
                  {d.lineItems.length}行 / status: {d.status}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3 max-h-[480px] overflow-y-auto">
            <div className="text-xs text-[var(--text-muted)]">納品: {target.deliveryNumber}</div>
            {editedItems?.map((item, itemIdx) => (
              <div
                key={itemIdx}
                className="rounded-md border border-[var(--border)] p-3 bg-[var(--bg-surface)] space-y-2"
              >
                <div className="text-sm font-medium">
                  {item.name}
                  <span className="text-xs text-[var(--text-muted)] ml-2">{item.modelName}</span>
                </div>
                {item.deviceKind === 'none' ? (
                  <p className="text-xs text-[var(--text-muted)]">
                    シリアルなし（機器ではない商品）
                  </p>
                ) : (
                  <div className="grid gap-1.5">
                    {item.serialNumbers.map((s, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-2">
                        <span className="text-xs text-[var(--text-muted)] w-6">#{sIdx + 1}</span>
                        <Input
                          value={s}
                          onChange={(e) => handleChangeSerial(itemIdx, sIdx, e.target.value)}
                          placeholder={serialFieldLabel(item.deviceKind)}
                          className="font-mono text-xs"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {target && (
            <>
              <Button variant="ghost" onClick={() => setSelectedDeliveryId(null)}>
                ← 戻る
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleSave(false)}
                disabled={update.isPending}
              >
                下書き保存
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSave(true)}
                disabled={!allFilled || update.isPending}
              >
                {update.isPending ? '更新中…' : '納品完了 (機器を登録)'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// 顧客の deliveries を collectionGroup で 1 リクエスト取得
function useContactDeliveries(contactId: string) {
  return useQuery({
    queryKey: ['deliveries', 'contact', contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<DeliveryWithDeal[]> => {
      const snap = await getDocs(
        query(
          collectionGroup(db, 'deliveries'),
          where('contactId', '==', contactId),
          orderBy('createdAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Delivery) }));
    },
  });
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}
